import { useMemo } from 'react';

import { EMPTY_ARRAY } from '@e-socna/empty';

import type { ThirdPartiesCmp_SomePerson } from '../queries/some-people.query';

import { useFindAllInternalPeopleQuery } from './useFindAllInternalPeopleQuery.hook';
import { useFindAllExternalPeopleQuery } from './useFindAllExternalPeopleQuery.hook';

interface UnionResult {
  readonly data:
    | Readonly<{ findSomePeople: readonly ThirdPartiesCmp_SomePerson[] }>
    | undefined;
  readonly isLoading: boolean;
  readonly isSuccess: boolean;
}

export const useFindAllPeopleQuery = (enabled = true): UnionResult => {
  const externalQuery = useFindAllExternalPeopleQuery(enabled);
  const internalQuery = useFindAllInternalPeopleQuery(enabled);

  const memoizedResult = useMemo(() => {
    const externalData = externalQuery.data?.findSomePeople;
    const internalData = internalQuery.data?.findSomePeople;

    const data =
      externalData || internalData
        ? {
            findSomePeople: [
              ...(externalData ?? EMPTY_ARRAY),
              ...(internalData ?? EMPTY_ARRAY),
            ],
          }
        : undefined;

    const isLoading = externalQuery.isLoading || internalQuery.isLoading;
    const isSuccess = externalQuery.isSuccess && internalQuery.isSuccess;

    return { data, isLoading, isSuccess };
  }, [
    externalQuery.data?.findSomePeople,
    externalQuery.isLoading,
    externalQuery.isSuccess,
    internalQuery.data?.findSomePeople,
    internalQuery.isLoading,
    internalQuery.isSuccess,
  ]);

  return memoizedResult;
};
